import {
  ChatAlt2Icon,
  DocumentTextIcon,
  GlobeAltIcon,
  PencilAltIcon,
  RefreshIcon,
} from '@heroicons/react/outline';
import useLocale from 'next-translate/useTranslation';
import type { ComponentType, SVGProps } from 'react';
import { FaYoutube } from 'react-icons/fa6';

import styles from './agents.module.css';

import clsxm from '@/lib/clsxm';

import { delay, SectionHeading, Stage, tone } from './primitives';
import { useReveal } from './useReveal';

type Icon = ComponentType<SVGProps<SVGSVGElement>>;

export default function AgentKnowledge() {
  const { t } = useLocale('chatbots');
  const { ref, state } = useReveal<HTMLDivElement>();

  const sourceTypes: { icon: Icon; name: string; detail: string }[] = [
    {
      icon: GlobeAltIcon,
      name: t('Websites and sitemaps'),
      detail: t('Crawl your pages or import a sitemap'),
    },
    {
      icon: DocumentTextIcon,
      name: t('Files'),
      detail: t('PDF, Word, CSV, TXT and EPUB'),
    },
    {
      icon: FaYoutube as Icon,
      name: t('YouTube videos'),
      detail: t('Learns from what is said in them'),
    },
    {
      icon: ChatAlt2Icon,
      name: t('Q&A pairs'),
      detail: t('Exact answers for the questions that matter most'),
    },
    {
      icon: PencilAltIcon,
      name: t('Plain text'),
      detail: t('Paste anything else it should know'),
    },
  ];

  return (
    <section id='knowledge' className='py-20 sm:py-28'>
      <div className='mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:items-center lg:gap-12 lg:px-8'>
        <div className='lg:col-span-5'>
          <SectionHeading
            size='md'
            title={t('It learns your business in minutes.')}
            tail={t('Then answers from it, not from guesswork.')}
            lead={t(
              'Add your website and documents, and write a few Q&As for the tricky questions. Your agent answers from what you gave it, and you can see the sources behind every reply.',
            )}
          />
          <ul className='mt-10 divide-y divide-[#0f0f28]/[0.07] border-y border-[#0f0f28]/[0.07] dark:divide-white/[0.07] dark:border-white/[0.07]'>
            {sourceTypes.map(({ icon: Icon, name, detail }) => (
              <li key={name} className='flex items-center gap-4 py-3.5'>
                <span
                  className={clsxm(
                    'flex h-9 w-9 flex-none items-center justify-center rounded-xl',
                    tone.surface,
                    tone.line,
                  )}
                >
                  <Icon className='h-[18px] w-[18px] text-primary-600 dark:text-primary-400' />
                </span>
                <span className='min-w-0'>
                  <span
                    className={clsxm(
                      'block text-[15px] font-semibold',
                      tone.ink,
                    )}
                  >
                    {name}
                  </span>
                  <span className={clsxm('block text-sm', tone.body)}>
                    {detail}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div
          ref={ref}
          data-reveal={state}
          className={clsxm(styles.reveal, 'lg:col-span-7')}
        >
          <Stage
            variant='lavender'
            halftoneAt='0% 100%'
            className='px-4 pb-6 pt-6 sm:px-10 sm:pb-12 sm:pt-12'
          >
            <p className='sr-only'>
              {t(
                'Example: an agent’s knowledge base with a website, files and Q&As, and an answer that lists the sources it used.',
              )}
            </p>
            <div aria-hidden='true' className='relative'>
              <div className={styles.rv} style={delay(0)}>
                <KnowledgePanel />
              </div>
              <div
                className={clsxm(
                  styles.rv,
                  'relative z-10 -mt-9 ms-auto w-[92%] sm:-mt-11 sm:w-[78%]',
                )}
                style={delay(220)}
              >
                <AnswerCard />
              </div>
            </div>
          </Stage>
        </div>
      </div>
    </section>
  );
}

function KnowledgePanel() {
  const { t } = useLocale('chatbots');

  const rows = [
    {
      icon: GlobeAltIcon as Icon,
      name: 'hotelmarisol.com',
      kind: t('48 pages'),
      done: true,
    },
    {
      icon: DocumentTextIcon as Icon,
      name: t('Cancellation policy.pdf'),
      kind: 'PDF',
      done: true,
    },
    {
      icon: DocumentTextIcon as Icon,
      name: t('Room types.docx'),
      kind: 'Word',
      done: true,
    },
    {
      icon: ChatAlt2Icon as Icon,
      name: t('Parking and check-in'),
      kind: 'Q&A',
      done: true,
    },
    {
      icon: FaYoutube as Icon,
      name: t('Hotel tour'),
      kind: 'YouTube',
      done: false,
    },
  ];

  return (
    <div
      className={clsxm(
        'rounded-2xl p-5 pb-14 shadow-[0_24px_60px_-30px_rgba(30,20,90,0.35)] sm:p-6 sm:pb-16',
        tone.surface,
        tone.line,
      )}
    >
      <div className='flex items-center justify-between gap-4'>
        <div>
          <p className={clsxm('text-[15px] font-semibold', tone.ink)}>
            {t('Knowledge')}
          </p>
          <p className={clsxm('text-xs', tone.quiet)}>
            {t('5 sources · updated today')}
          </p>
        </div>
        <span
          className={clsxm(
            'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold',
            tone.ink,
            tone.line,
          )}
        >
          <RefreshIcon className='h-3.5 w-3.5' />
          {t('Retrain')}
        </span>
      </div>

      <div className='mt-5'>
        <div className='flex justify-between text-xs'>
          <span className={tone.body}>{t('Characters used')}</span>
          <span className={clsxm('tabular-nums', tone.quiet)}>1.2M / 10M</span>
        </div>
        <div className='mt-2 h-1.5 overflow-hidden rounded-full bg-[#ececf3] dark:bg-white/10'>
          <div className='h-full w-[12%] rounded-full bg-primary-600' />
        </div>
      </div>
      <ul className='mt-5 space-y-2'>
        {rows.map(({ icon: Icon, name, kind, done }) => (
          <li
            key={name}
            className='flex items-center gap-3 rounded-xl bg-[#f7f7fa] px-3.5 py-2.5 dark:bg-white/[0.04]'
          >
            <Icon className={clsxm('h-4 w-4 flex-none', tone.quiet)} />
            <span
              className={clsxm(
                'min-w-0 flex-1 truncate text-sm font-medium',
                tone.ink,
              )}
            >
              {name}
            </span>
            <span className={clsxm('hidden text-xs sm:inline', tone.quiet)}>
              {kind}
            </span>
            {done ? (
              <span className='rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-400/10 dark:text-emerald-300'>
                {t('Trained')}
              </span>
            ) : (
              <span className='rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:bg-amber-400/10 dark:text-amber-300'>
                {t('Training')}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function AnswerCard() {
  const { t } = useLocale('chatbots');

  return (
    <div
      className={clsxm(
        'rounded-2xl p-5 shadow-[0_30px_70px_-25px_rgba(30,20,90,0.45)]',
        tone.surface,
        tone.line,
      )}
    >
      <p
        className={clsxm(
          'mb-3 flex items-center gap-1.5 text-xs font-medium',
          tone.quiet,
        )}
      >
        <ChatAlt2Icon className='h-3.5 w-3.5' />
        {t('Conversations')} · {t('Website')}
      </p>
      <div className='flex justify-end'>
        <p
          className={clsxm(
            'max-w-[85%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-4 py-2 text-sm dark:bg-white/[0.07]',
            tone.ink,
          )}
        >
          {t('Can I cancel for free?')}
        </p>
      </div>
      <p className='mt-3 max-w-[92%] rounded-2xl rounded-es-md bg-[#b64d27] px-4 py-2.5 text-sm leading-relaxed text-white'>
        {t(
          'Yes, cancellation is free up to 48 hours before arrival. After that, the first night is charged.',
        )}
      </p>
      <div className='mt-4 flex flex-wrap items-center gap-2 border-t border-[#0f0f28]/[0.06] pt-3.5 dark:border-white/[0.07]'>
        <span className={clsxm('text-xs font-medium', tone.quiet)}>
          {t('Sources')}
        </span>
        <span className='inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 dark:bg-primary-400/10 dark:text-primary-300'>
          <DocumentTextIcon className='h-3.5 w-3.5' />
          {t('Cancellation policy.pdf')}
        </span>
        <span className='inline-flex items-center gap-1.5 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-700 dark:bg-primary-400/10 dark:text-primary-300'>
          <GlobeAltIcon className='h-3.5 w-3.5' />
          hotelmarisol.com/faq
        </span>
      </div>
    </div>
  );
}
